import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import { legacyTheme, styled } from '@prose-ui/legacy';

import { RadioGroup } from '../Components/RadioGroup';

import consultationCategoryProp from 'PropTypes/consultationProps';

import * as consultationSelectors from 'dux/consultation/selectors';

import { QuestionLayout } from './QuestionLayout';

/**
 * Container for placing the radio group sets
 */
const RadioGroupContainer = styled.div`
  flex: 1 0 auto;
  padding: ${legacyTheme.spacing.s16} ${legacyTheme.spacing.s8};
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-content: center;
  gap: ${legacyTheme.spacing.s56};

  /* different presentation on desktop */
  ${legacyTheme.breakpoints.up('md')} {
    padding: ${legacyTheme.spacing.s8};
    gap: ${legacyTheme.spacing.s36};
    max-width: 900px;

    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-around;
    align-content: space-evenly;

    & > * {
      max-width: 355px;
    }
  }
`;

export const SkincareDiet = ({
  consultationCategory,
  cta,
  initialValue,
  onNext,
  labels,
  descriptions,
  options,
  questionSet,
  ...props
}) => {
  const [values, setValues] = useState(initialValue ?? Array(labels.length).fill(undefined));

  const disabled = useMemo(() => values.some(v => !v), [...values]);
  const submit = useCallback(() => {
    onNext(values);
  }, [...values]);

  const onChecked = useCallback(
    opt => {
      const idx = labels.indexOf(opt.name);
      setValues(vals =>
        vals.map((v, id) => {
          if (id === idx) {
            return opt.value;
          }
          return v;
        })
      );
    },
    [...labels, ...values]
  );

  return (
    <QuestionLayout
      {...props}
      buttonLabel={cta}
      consultationCategory={consultationCategory}
      disabled={disabled}
      multi={false}
      questionSet={questionSet}
      showProgressTracker={!questionSet?.options?.showSteps ?? true}
      showSteps={questionSet?.options?.showSteps ?? false}
      submit={submit}
    >
      <RadioGroupContainer>
        {labels.map((label, idx) => (
          <RadioGroup
            key={label}
            descriptions={descriptions}
            name={label}
            onChange={onChecked}
            options={options}
            selected={values[idx]}
          />
        ))}
      </RadioGroupContainer>
    </QuestionLayout>
  );
};

SkincareDiet.defaultProps = {
  labels: [],
  initialValue: null,
};

SkincareDiet.propTypes = {
  consultationCategory: consultationCategoryProp.isRequired,
  cta: PropTypes.string.isRequired,
  initialValue: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  onNext: PropTypes.func.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string),
  descriptions: PropTypes.shape({
    never: PropTypes.string,
    between: PropTypes.string,
    always: PropTypes.string,
  }).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.shape()]).isRequired,
      value: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  // questionSet is an array with a an optional property "options"
  questionSet: PropTypes.oneOfType([
    // Union
    PropTypes.arrayOf(PropTypes.shape({})), // Array Of Question
    PropTypes.shape({ options: PropTypes.shape({ showSteps: PropTypes.bool }) }), // options property
  ]).isRequired,
};

export default connect(
  (state, { consultationCategory, consultationSubsetCategory, questionIndex, routeParams }) => ({
    ...consultationSelectors.getConsultationQuestionForIndex(state, {
      consultationCategory,
      consultationSubsetCategory,
      questionIndex,
      routeParams,
    }),
    options: consultationSelectors.getOptionsForQuestion(state, {
      consultationCategory,
      consultationSubsetCategory,
      questionIndex,
      routeParams,
    }),
    tipContent: consultationSelectors.getTipForQuestion(state, {
      consultationCategory,
      consultationSubsetCategory,
      questionIndex,
      routeParams,
    }),
  })
)(SkincareDiet);
